import type { ComponentProps } from 'react';
import { useCallback, useState } from 'react';

import type { PageMessage } from '@change-corgi/design-system-legacy/components/alerts';

// eslint-disable-next-line @change/sibling-module-imports
import type { Action } from '#src/app/pages/starterDashboardV2/components/PageBody/components/PageBanner/types';

type Banner = Omit<ComponentProps<typeof PageMessage>, 'primaryAction' | 'secondaryAction'> & {
	primaryAction?: Action;
	secondaryAction?: Action;
};

export type UseBannerStateReturn = ModelHookResult<
	{ currentBanner: Banner | undefined },
	{ addBanner: (banner: Banner) => void; dismissBanner: () => void }
>;

export function useBannerState(): UseBannerStateReturn {
	const [bannerStack, setBannerStack] = useState<Banner[]>([]);

	const handleAddBanner = useCallback((banner: Banner) => {
		setBannerStack((stack) => {
			// Prevent duplicate banners by checking if a banner with the same message already exists
			const isDuplicate = stack.some(
				(existingBanner) => existingBanner.message === banner.message && existingBanner.severity === banner.severity,
			);

			if (isDuplicate) {
				return stack; // Don't add duplicate
			}
			return [banner, ...stack];
		});
	}, []);

	const handleDismissBanner = useCallback(() => {
		setBannerStack(([_, ...rest]) => rest);
	}, []);

	const currentBanner = bannerStack.at(0);

	return { data: { currentBanner }, actions: { addBanner: handleAddBanner, dismissBanner: handleDismissBanner } };
}
